
"use client";

import React, { useState, useEffect } from "react";
import { Handle, Position, NodeResizer, NodeToolbar } from '@xyflow/react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import EditNodeDialog from "./EditNodeDialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";

interface NodeLabel {
  type: string;
  title: string;
  description: string;
}

interface CustomNodeProps {
  data: {
    label: NodeLabel | React.ReactNode;
    onNodeDoubleClick?: (event: React.MouseEvent<HTMLDivElement>, nodeId: string) => void;
    isSelected?: boolean;
    title?: string;
    description?: string;
    kpiType?: string;
    fullDescription?: string;
    onSave?: (title: string, description: string) => Promise<void>;
    onDelete?: () => Promise<void>;
    companyId?: string;
  };
  id: string;
  selected?: boolean;
}

const CustomNode: React.FC<CustomNodeProps> = ({ data, id, selected }) => {
  
  const [showDialog, setShowDialog] = useState(false);
  const [showRestrictedAlert, setShowRestrictedAlert] = useState(false);
  const [nodeTitle, setNodeTitle] = useState(data.title || "");
  const [nodeDescription, setNodeDescription] = useState(data.description || "");
  
  // Refresh node data from Supabase when it changes
  useEffect(() => {
    if (data.title) setNodeTitle(data.title);
    if (data.description) setNodeDescription(data.description);
  }, [data.title, data.description]);
  
  const handleDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    
    console.log("Double click detected in CustomNode for node:", id);
    console.log("Node KPI Type:", data.kpiType);
    
    if (id.startsWith('kpi-')) {
      // Check if this is a Potential Solutions node (editable) or other KPI types (non-editable)
      const isEditableNode = data.kpiType === 'Potential Solutions' || 
                             (!data.kpiType && id.includes('potential')) ||
                             (!data.kpiType && !id.includes('exec') && !id.includes('financial') && !id.includes('initiative') && !id.includes('operating'));
      
      if (isEditableNode && data.title && data.description) {
        setShowDialog(true);
      } else if (data.kpiType && (
        data.kpiType === 'Executive KPIs' || 
        data.kpiType === 'Core Financial KPIs' || 
        data.kpiType === 'Business Initiatives' || 
        data.kpiType === 'Operating KPIs'
      )) {
        // Show alert for non-editable KPI types
        setShowRestrictedAlert(true);
      } else if (data.title && data.description) {
        // Default behavior for nodes with title and description
        setShowDialog(true);
      }
    } else if (data.onNodeDoubleClick) {
      data.onNodeDoubleClick(event, id);
    }
  };

  const handleDialogClose = () => {
    setShowDialog(false);
  };

  const handleRestrictedAlertClose = () => {
    setShowRestrictedAlert(false);
  };

  const handleSaveNode = async (newTitle: string, newDescription: string) => {
    try {
      // First, call the parent onSave function if provided
      if (data.onSave) {
        await data.onSave(newTitle, newDescription);
      }
      
      // Update local state to refresh the node display
      setNodeTitle(newTitle);
      setNodeDescription(newDescription);
      
      return Promise.resolve();
    } catch (error) {
      console.error("Error in handleSaveNode:", error);
      return Promise.reject(error);
    }
  };

  // Truncate text for display in the node (1 line title, 2 lines description)
  const truncateForDisplay = (text: string, maxLines: number = 2) => {
    if (!text) return "";
    
    // Estimate characters per line based on node width and font size
    const charsPerLine = 25; // Approximate for the node width and font size
    const maxChars = charsPerLine * maxLines;
    
    if (text.length <= maxChars) return text;
    return text.substring(0, maxChars - 3) + "...";
  };
  
  // Update the hover card configuration to always display on the left
  const getHoverCardConfig = () => {
    // Always display the hover card on the left side for all KPI types
    return {
      side: 'left' as const,
      align: 'center' as const,
      sideOffset: 10
    };
  };
  
  const hoverConfig = getHoverCardConfig();

  // Render the label based on its type
  const renderLabel = () => {
    // If the label is a NodeLabel object with type 'node-label'
    if (data.label && typeof data.label === 'object' && 'type' in data.label && data.label.type === 'node-label') {
      const nodeLabel = data.label as NodeLabel;
      return (
        <div className="flex flex-col gap-1 h-full justify-start">
          <div className="font-medium border-b border-gray-200 pb-1 text-xs text-[#9b87f5] leading-tight">
            {truncateForDisplay(nodeLabel.title, 1)}
          </div>
          <div className="text-xs text-gray-600 overflow-hidden leading-tight">
            {truncateForDisplay(nodeLabel.description || "", 2)}
          </div>
        </div>
      );
    }
    
    // Otherwise, render the label as is (supports backward compatibility)
    return data.label;
  };
  
  // Create a properly typed label content that ensures ReactNode output
  const LabelContent = () => {
    return renderLabel() as React.ReactElement;
  };
  
  return (
    <>
      <Handle type="target" position={Position.Top} id="top" className="!top-0" />
      <NodeResizer maxWidth={200} maxHeight={80} isVisible={true} lineStyle={{
        background: '#ddd'
      }} handleStyle={{
        background: '#fff',
        border: '1px solid #ddd'
      }} />
      
      {id.startsWith('kpi-') && data.title && data.description && (
        <NodeToolbar position={Position.Top} offset={10} isVisible={showDialog}>
          <EditNodeDialog 
            isOpen={showDialog}
            onClose={handleDialogClose}
            nodeId={id}
            initialTitle={data.title}
            initialDescription={data.description}
            onSave={handleSaveNode}
            onDelete={data.onDelete || (() => Promise.resolve())}
            companyId={data.companyId}
          />
        </NodeToolbar>
      )}
      
      <div 
        className="relative h-full w-full cursor-pointer flex flex-col" 
        onDoubleClick={handleDoubleClick}
        data-testid={`node-${id}`}
      >
        {data.kpiType && nodeTitle ? (
          <HoverCard>
            <HoverCardTrigger asChild>
              <div className="h-full flex flex-col">
                <LabelContent />
              </div>
            </HoverCardTrigger>
            <HoverCardContent 
              className="w-[420px] bg-black text-white border-none p-4 max-w-none" 
              side={hoverConfig.side}
              align={hoverConfig.align}
              sideOffset={hoverConfig.sideOffset}
              style={{ zIndex: 10000 }}
            >
              <div className="space-y-3">
                <div className="font-bold text-base">{data.kpiType}</div>
                <div className="font-semibold text-base break-words whitespace-pre-wrap">{nodeTitle}</div>
                <div className="text-[15px] leading-relaxed break-words whitespace-pre-wrap">{data.fullDescription || nodeDescription}</div>
              </div>
            </HoverCardContent>
          </HoverCard>
        ) : (
          <div className="h-full flex flex-col"><LabelContent /></div>
        )}
      </div>
      
      {/* Alert Dialog for Restricted Editing */}
      <AlertDialog open={showRestrictedAlert} onOpenChange={setShowRestrictedAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Node Cannot Be Edited</AlertDialogTitle>
            <AlertDialogDescription>
              Only Potential Solutions nodes can be edited. Executive KPIs, Core Financial KPIs, Business Initiatives, and Operating KPIs nodes are read-only.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleRestrictedAlertClose}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <Handle type="source" position={Position.Bottom} id="bottom" className="!bottom-0" />
    </>
  );
};

export default CustomNode;
